import React, { useState } from 'react'
import { Button, Input, Popconfirm, Select, Space, message } from 'antd'
import styled from 'styled-components'
import { Text } from '../../../styles/reactnative'
import API from '../../../utils/api'
import ExchangeName from '../ExchangeName'
import Price from '../PositionColumns/Price'
import PSlipage from '../PSlipage'

// 스타일 컴포넌트들
const CardContainer = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-color: #1890ff;
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const PairInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const PairCode = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #1890ff;
`

const GroupName = styled.div`
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
`

const PersonalBadge = styled.div`
  background: #e6f7ff;
  color: #1890ff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`

const CardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const Section = styled.div`
  background: #fafafa;
  border-radius: 6px;
  padding: 12px;
`

const SectionTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FieldRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const FieldLabel = styled.span`
  font-size: 13px;
  color: #666;
  min-width: 80px;
`

const FieldValue = styled.div`
  flex: 1;
  text-align: right;
`

const EditInput = styled(Input)`
  width: 120px !important;
  text-align: right;
`

const ActionSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SlippageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

interface PersonalContractCardProps {
  record: any
  edit: boolean
  setEdit: (edit: boolean) => void
  mutate: (value: any) => void
  shareMutate: (value: any) => void
  token: string
  uid: string
  level: number
}

const PersonalContractCard: React.FC<PersonalContractCardProps> = ({
  record,
  edit,
  setEdit,
  mutate,
  shareMutate,
  token,
  uid,
  level
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localRecord, setLocalRecord] = useState({ ...record })

  const handleEdit = () => {
    if (!isEditing) {
      setIsEditing(true)
      setLocalRecord({ ...record })
    } else {
      // 저장 로직
      API.send('PUT', '/admin/personpairs', token || '', {
        ov: localRecord?.pairs,
        userid: uid,
        margin: localRecord?.minimum_margin,
        losscut: localRecord?.losscut,
        ovnmargin: localRecord?.ovn_margin,
        max_amount: localRecord?.max_amount,
        fee: localRecord?.fee,
        feetype: localRecord?.feetype,
        slip: localRecord?.slip,
      }).then(res => {
        setIsEditing(false)
        message.success('수정되었습니다.')
        mutate(1)
        shareMutate(1)
      })
    }
  }

  const handleGroupEdit = () => {
    API.send('PUT', '/admin/grouppairsnotset', token || '', {
      ov: localRecord?.pairs,
      margin: localRecord?.minimum_margin,
      losscut: localRecord?.losscut,
      ovnmargin: localRecord?.ovn_margin,
      max_amount: localRecord?.max_amount,
      fee: localRecord?.fee,
      feetype: localRecord?.feetype,
      slip: localRecord?.slip,
    }).then(res => {
      setIsEditing(false)
      message.success('그룹일괄 수정되었습니다.')
      mutate(1)
      shareMutate(1)
    })
  }

  const handleDelete = () => {
    API.delete('/admin/personpairs', token || '', {
      ov: record?.pairs,
      userid: uid,
    }).then(res => {
      message.success('삭제되었습니다.')
      mutate(1)
      shareMutate(1)
    })
  }

  const updateField = (field: string, value: any) => {
    setLocalRecord(prev => ({ ...prev, [field]: value }))
  }

  return (
    <CardContainer>
      <CardHeader>
        <PairInfo>
          <PairCode>{record?.pairs}</PairCode>
          <ExchangeName exchange_id={record?.exchange_id} />
          <GroupName>{record?.groupname}</GroupName>
          {record?.person && <PersonalBadge>개인설정</PersonalBadge>}
        </PairInfo>
        <Text style={{ fontSize: '12px', color: '#999' }}>
          코드: {record?.code}
        </Text>
      </CardHeader>

      <CardBody>
        {/* 증거금 섹션 */}
        <Section>
          <SectionTitle>💰 증거금 설정</SectionTitle>
          
          <FieldRow>
            <FieldLabel>증거금</FieldLabel>
            <FieldValue>
              {isEditing ? (
                <EditInput
                  type="number"
                  defaultValue={localRecord?.minimum_margin}
                  onChange={e => updateField('minimum_margin', e.target.value)}
                />
              ) : (
                <Price price={record?.minimum_margin} dp={0} />
              )}
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>로스컷</FieldLabel>
            <FieldValue>
              {isEditing ? (
                <EditInput
                  type="number"
                  defaultValue={localRecord?.losscut}
                  onChange={e => updateField('losscut', e.target.value)}
                />
              ) : (
                <Price price={record?.losscut} dp={0} />
              )}
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>오버나잇</FieldLabel>
            <FieldValue>
              {isEditing ? (
                <EditInput
                  type="number"
                  defaultValue={localRecord?.ovn_margin}
                  onChange={e => updateField('ovn_margin', e.target.value)}
                />
              ) : (
                <Price price={record?.ovn_margin} dp={0} />
              )}
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>최대계약수</FieldLabel>
            <FieldValue>
              {isEditing ? (
                <EditInput
                  type="number"
                  defaultValue={localRecord?.max_amount}
                  onChange={e => updateField('max_amount', e.target.value)}
                />
              ) : (
                <Text>{record?.max_amount}</Text>
              )}
            </FieldValue>
          </FieldRow>
        </Section>

        {/* 수수료 섹션 */}
        <Section>
          <SectionTitle>💳 수수료 설정</SectionTitle>
          
          <FieldRow>
            <FieldLabel>수수료</FieldLabel>
            <FieldValue>
              {isEditing ? (
                <EditInput
                  type="number"
                  defaultValue={localRecord?.fee}
                  onChange={e => updateField('fee', e.target.value)}
                />
              ) : (
                <Text>{record?.fee}</Text>
              )}
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>단위</FieldLabel>
            <FieldValue>
              {isEditing ? (
                <Select
                  defaultValue={localRecord?.feetype}
                  size="small"
                  style={{ width: 80 }}
                  options={[
                    { value: 0, label: '%' },
                    { value: 1, label: record?.feecurrency },
                  ]}
                  onChange={value => updateField('feetype', value)}
                />
              ) : (
                <Text>{record?.feetype == 0 ? '%' : record?.feecurrency}</Text>
              )}
            </FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>슬리피지</FieldLabel>
            <FieldValue>
              <SlippageContainer>
                <PSlipage user_id={uid} ep_id={record?.id} />
              </SlippageContainer>
            </FieldValue>
          </FieldRow>
        </Section>
      </CardBody>

      <ActionSection>
        <div>
          <Text style={{ fontSize: '12px', color: '#999' }}>
            종목 ID: {record?.id}
          </Text>
        </div>
        
        <Space>
          {level == 99 && (
            <Button
              type="primary"
              size="small"
              onClick={handleGroupEdit}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              그룹일괄
            </Button>
          )}

          <Button
            type={isEditing ? 'primary' : 'default'}
            size="small"
            onClick={handleEdit}
          >
            {isEditing ? '저장' : '수정'}
          </Button>

          {isEditing && (
            <Button
              size="small"
              onClick={() => {
                setIsEditing(false)
                setLocalRecord({ ...record })
              }}
            >
              취소
            </Button>
          )}

          <Popconfirm
            title="개별설정을 삭제하시겠습니까?"
            onConfirm={handleDelete}
            okText="삭제"
            cancelText="취소"
          >
            <Button type="primary" size="small" danger>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      </ActionSection>
    </CardContainer>
  )
}

export default PersonalContractCard